// 工具函数集合

/**
 * 取整方法
 * @param {number} num - 要取整的数字
 * @param {string} type - 取整类型：'ceil'向上取整, 'floor'向下取整, 'round'四舍五入，'int'只取整数
 * @returns {number} 取整后的结果
 */
export function roundNumber(num, type = 'round') {
  const n = Number(num);
  if (isNaN(n)) return 0;

  switch (type) {
    case 'int':
      return Math.floor(n);
    case 'ceil':
      return Math.ceil(n);
    case 'floor':
      return Math.floor(n);
    case 'round':
    default:
      return Math.round(n);
  }
}

/**
 * 保留小数点后两位数的方法
 * @param {number|string} num - 要处理的数字
 * @param {boolean} needRound - 是否需要四舍五入，默认为true
 * @returns {number} 保留两位小数后的结果
 */
export function keepTwoDecimalPlaces(num, needRound = true) {
  const n = Number(num);
  if (isNaN(n)) return 0;

  if (needRound) {
    // 四舍五入保留两位小数
    return Math.round(n * 100) / 100;
  } else {
    // 直接截取两位小数，不四舍五入
    return Math.floor(n * 100) / 100;
  }
}

/**
 * 计算时间差的方法
 * @param {Date|string|number} startTime - 开始时间
 * @param {Date|string|number} endTime - 结束时间，默认为当前时间
 * @param {string} unit - 返回的时间单位：'ms'毫秒, 's'秒, 'm'分钟, 'h'小时, 'd'天
 * @returns {number} 计算后的时间差
 */
export function calculateTimeDifference(startTime, endTime = new Date(), unit = 'ms') {
  const start = new Date(startTime).getTime();
  const end = new Date(endTime).getTime();

  if (isNaN(start) || isNaN(end)) {
    console.error('Invalid time format');
    return 0;
  }

  const diff = Math.abs(end - start);

  switch (unit) {
    case 's':
      return diff / 1000;
    case 'm':
      return diff / (1000 * 60);
    case 'h':
      return diff / (1000 * 60 * 60);
    case 'd':
      return diff / (1000 * 60 * 60 * 24);
    case 'ms':
    default:
      return diff;
  }
}

/**
 * 计算时间差并格式化显示
 * @param {Date|string|number} startTime - 开始时间
 * @param {Date|string|number} endTime - 结束时间，默认为当前时间
 * @returns {string} 格式化的时间差字符串，如：'3天2小时15分钟'
 */
export function formatTimeDifference(startTime, endTime = new Date()) {
  const diffMs = calculateTimeDifference(startTime, endTime, 'ms');

  const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((diffMs % (1000 * 60)) / 1000);

  let result = '';
  if (days > 0) result += `${days}天`;
  if (hours > 0) result += `${hours}小时`;
  if (minutes > 0) result += `${minutes}分钟`;
  if (seconds > 0 && days === 0 && hours === 0) result += `${seconds}秒`;

  return result || '0秒';
}

/**
 * 格式化日期方法
 * @param {Date|string} dateString - 日期是十位时间戳，如：1694502400000
 * @param {string} format - 日期格式，默认为'YYYY-MM-DD'
 * @returns {string} 格式化后的日期字符串
 */
export function formatDate(dateString, format = 'YYYY-MM-DD') {
  if (!dateString) {
    return '';
  }
  // 时间戳转成日期对象
  // 时间戳是十位，需要乘以1000
  dateString = Number(dateString) * 1000;

  const date = new Date(Number(dateString));
  if (isNaN(date.getTime())) {
    console.error('Invalid date format');
    return '';
  }

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');

  return format
    .replace('YYYY', year)
    .replace('MM', month)
    .replace('DD', day)
    .replace('HH', hours)
    .replace('mm', minutes)
    .replace('ss', seconds);
}

/**
 * 格式化价格方法
 * @param {number|string} price - 价格数值或字符串
 * @param {number} decimals - 保留的小数位数，默认为2
 * @returns {string} 格式化后的价格字符串
 */
export function formatPrice(price, decimals = 2) {
  const num = Number(price);
  if (isNaN(num)) {
    console.error('Invalid price format');
    return '0.00';
  }

  return num.toFixed(decimals);
}

// 导出所有方法
export default {
  roundNumber,
  keepTwoDecimalPlaces,
  calculateTimeDifference,
  formatTimeDifference,
  formatDate,
  formatPrice
};



// WEBPACK FOOTER //
// ./src/utils/tool.js